/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useEffect, useState } from "react";
import "../../../assets/styles/custom-style.css";
import "../../../assets/styles/custom-style.scss";


import { useAuth } from "../../../auth";
import {
  listOrdersData,
  getStatus,
  changeStatusOrders,
  viewTracking,
} from "../core/_requests";
import { useNavigate } from "react-router-dom";
import CustomNotification from "./notification";
import MenuOrders from "./MenuOrders";
import Avatar from "react-avatar";
import { OrderProps } from "../core/_models";


interface Status {
  id: number;
  name: string;
  active?: boolean;
}

interface Track {
  id: number;
  quoteId: string;
  deliveryId: string;
  trackingId: string | null;
  deliveryPartnerType: number;
  deliveryPerson: string | null;
  contactNumber: string | null;
  vehicleColor: string | null;
  vehicleType: string | null;
  driverLatitude: number | null;
  driverLongitude: number | null;
  trackingStatus: string;
  trackingURL: string;
  deliveryOtp: string;
  status: number;
}
// Define order types
const orderTypes = [
  { id: 1, name: "Regular" },
  // { id: 2, name: "Bulk" },
  { id: 3, name: "Scheduled" },
];
const OfflineOrders = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [listOrders, setListOrders] = useState<OrderProps[]>([]);
  const [status, setStatus] = useState<Status[]>([]);
  const [loading, setLoading] = useState(true);
  const [chefStatus, setChefStatus] = useState<boolean>();
  const chefId = currentUser?.uuid;
  const [selectedOrderType, setSelectedOrderType] = useState<number>(1); // Default to 'Default'
  // Filter orders based on the selected order type
  const filteredOrders = listOrders.filter(
    (order) => order.order_type === selectedOrderType
  );
  useEffect(() => {
    // Fetch chef's current availability status
    listOrdersData(chefId)
      .then((res) => {
        const isChefAvailable = res.data?.currentlyAvailableStatus ?? false;
        setChefStatus(isChefAvailable); // Set chef status based on the currentlyAvailableStatus
        // Show notification based on chef's availability
        setNotificationMessage(
          isChefAvailable ? "You are online now" : "You are offline"
        );
        setShowNotification(true);
      })
      .catch((error) => {
        console.error("Error fetching chef status:", error);
      });
  }, [chefId]);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  useEffect(() => {
    getStatus()
      .then((res) => {
        const statusData = res?.data?.data.map((s: any) => ({
          ...s,
          active: s.name === "ordered", // Set active to true for "ordered" status
        }));
        setStatus(statusData);
      })
      .catch((error) => {
        console.error("Error fetching order statuses:", error);
      });
  }, []);
  useEffect(() => {
    listOrdersData(chefId).then((res) => {
      setListOrders(res?.data);
      setLoading(false);
    });
  }, [chefId]);
  useEffect(() => {
    if (chefStatus) {
      setNotificationMessage("You are online now");
    } else {
      setNotificationMessage("You are offline");
    }

    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(true);
    }, 60000);
  }, [chefStatus]);
  const [trackingInfo, setTrackingInfo] = useState<Record<string, Track>>({});
  
  useEffect(() => {
    // Fetch tracking information for each order
    listOrders.forEach((order) => {
      viewTracking(order.uuid)
        .then((res) => {
          // Update tracking info state
          setTrackingInfo((prevTrackingInfo) => ({
            ...prevTrackingInfo,
            [order.uuid]: res?.data,
          }));
        })
        .catch((error) => {
          console.error("Error fetching tracking info:", error);
        });
    });
  }, [listOrders]);
  const handleOrderStatusChange = async (order: any, action: any) => {
    try {
      // Check if the chef is currently available
      if (!chefStatus) {
        window.alert(
          "You are currently offline. Please go online to change order status."
        );
        return;
      }

      let newOrderStatusId;

      // Map the action to the corresponding status ID
      switch (action) {

        case "Cancel Order":
          newOrderStatusId = 6;
          break;
        case "Preparing":
          newOrderStatusId = 2;
          break;
        case "Order Ready":
          newOrderStatusId = 3;
          break;
        // case "Pickup":
        //   newOrderStatusId = 5;
        //   break;
        default:
          console.warn("Unknown action:", action);
          return;
      }

      // Make an API call to change the order status
      await changeStatusOrders(order.uuid, { orderStatus: newOrderStatusId });

      // Optionally, fetch the updated list of orders after the status change
      await listOrdersData(chefId).then((res) => {
        setListOrders(res?.data);
        setLoading(false);
      });
            // Redirect to the appropriate page after changing the order status
      navigate("/orders/view");
    } catch (error) {
      console.error("Error handling order status change:", error);
    }
  };

  const getButtonLabel = (name: string): string[] => {
    switch (name) {
      case "ordered":
        return ["Accept Order", "Cancel Order"];

      case "preparing":
        return ["Order Ready"];
      case "ready":
      // return ["Pickup"];
      case "track":

      case "delivered":
      case "canceled":

      default:
        return [];
    }
  };
  const openHistory = () => {
        navigate("/orders/history");
  };
  return (
    <>
      <section className="container-fluid">
        <div className="row">


          <section className="content-section">
            {/*  */}

            <section className="row main-content">
              <MenuOrders />

              <div className="col-12 mt-2">
                {/* Order Types Navigation */}
                <ul className="nav nav-pills mb-4">
                  {orderTypes.map((type: any) => (
                    <li className="nav-item" key={type.id}>
                      <button
                        className={`mt-3 nav-link ${selectedOrderType === type.id ? "active" : ""
                          } ${selectedOrderType === type.id
                            ? "" // No color for active state
                            : type.id === 1
                              ? "text-success border-success font-weight-bold" // Green for Regular
                              : type.id === 3
                                ? "text-danger border-danger font-weight-bold" // Red for Scheduled
                                : "" // Default style for other types
                          } border`} // Add a default border class
                        onClick={() => setSelectedOrderType(type.id)}
                      >
                        {`${type.name} (${listOrders.filter(
                          (item) => item.order_type === type.id
                        ).length
                          })`}
                      </button>
                    </li>
                  ))}


                </ul>
                <ul
                  className="nav nav-pills mb-4"
                  id="pills-tab"
                  role="tablist"
                >
                  {status.slice().map((s) => (
                    <li className="nav-item" role="presentation" key={s.id}>
                      <button
                        className={`mt-3 nav-link ${s.active ? "active" : ""}`}
                        id={`pills-${s.name.toLowerCase()}-tab`}
                        data-bs-toggle="pill"
                        data-bs-target={`#pills-${s.name.toLowerCase()}`}
                        type="button"
                        role="tab"
                        aria-controls={`pills-${s.name.toLowerCase()}`}
                        aria-selected={s.active}
                      >
                        {`${s.name.charAt(0).toUpperCase() + s.name.slice(1)} (${filteredOrders.filter(
                          (item) => item.orderstatusId === s.id
                        ).length})`}
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="tab-content">
                  {status.slice().map((s) => (
                    <div
                      className={`tab-pane fade show${s.active ? " active" : ""
                        }`}
                      id={`pills-${s.name.toLowerCase()}`}
                      key={s.id}
                    >
                      {filteredOrders
                        .filter((item) => item.orderstatusId === s.id)

                        .map((item) => (
                          <div
                            className="tab-pane fade show active"
                            id={`pills-${s.name.toLowerCase()}-${item.primaryId
                              }`}
                            key={item.primaryId}
                          >
                            <div className="order-snippet mb-3">
                              <div className="os-head">
                                <div>
                                  <h5 className="mb-0 medium primary-color">
                                    Order - {item?.orderId}
                                  </h5>
                                  <p className="mb-0 mid-gray">
                                    <small>{item?.orderTime}</small>
                                  </p>
                                </div>
                                <div>
                                  {item?.order_type === 3 && (
                                    <div className="card mb-3" style={{ border: "1px solid red", borderRadius: "8px", padding: "16px" }}>
                                      <h5 className="mb-0 medium primary-color">
                                        Scheduled Date
                                        <p className="mb-0 mid-gray">
                                          <small>
                                            {item?.scheduled_date} - {item?.scheduled_time}
                                          </small>
                                        </p>
                                      </h5>
                                      <h5 className="mb-0 medium primary-color">
                                        Scheduled Order
                                      </h5>
                                    </div>
                                  )}
                                </div>
                                <div className="order-by">
                                  <div className="avatar me-4">
                                    <div className="thumbnail me-2">
                                      {item?.user?.userProfile ? (
                                        <Avatar
                                          src={item?.user?.userProfile}
                                          name={item?.user?.name}
                                          maxInitials={1}
                                          size="25"
                                          round
                                        ></Avatar>
                                      ) : (
                                        <Avatar
                                          name={item?.user?.name}
                                          maxInitials={1}
                                          size="25"
                                          round
                                          color="#607458"
                                          fgColor="#ffffff"
                                        ></Avatar>
                                      )}
                                    </div>
                                    <p className="mb-0">
                                      <span className="tiny mid-gray">
                                        Order by{" "}
                                      </span>
                                      <br />
                                      <small>{item?.user?.name}</small>
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="row os-body mt-3">
                                <div className="col-6">
                                  <h6 className="mb-2">Item Details</h6>
                                  {item?.orderdetails.map((orderDetail, j) => (
                                    <div className="bg-blk" key={j}>
                                      <div className="row mb-3">
                                        <div className="col-8">
                                          <div className="dish">
                                            <div className="thumbnail me-2">
                                              <img
                                                src={
                                                  orderDetail.dish?.dishImage
                                                }
                                                alt={orderDetail.dish?.name}
                                              />
                                            </div>
                                            <p className="mb-0">
                                              <span className="fw-500">
                                                {orderDetail.quantity}
                                              </span>{" "}
                                              x {orderDetail.dish?.name}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="col-4 d-flex align-items-center justify-content-end">
                                          <h6 className="mb-0">
                                            ${item?.totalAmount.toFixed(2)}
                                          </h6>
                                        </div>
                                      </div>

                                      <div className="row mb-3">
                                        <p className="px-0 mb-1">
                                          <small className="fw-500 px-2">
                                            Cooking Instructions
                                          </small>
                                        </p>
                                        <p className="px-0 mb-0 px-2">
                                          <span className="tiny mid-gray">
                                            {item.cookingInstruction}
                                          </span>
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="col-6">
                                  <h6 className="mb-2">&nbsp;</h6>
                                  <div
                                    className="accordion bill-toggle px-0"
                                    id="accordionExample"
                                  >
                                    <div className="accordion-item">
                                      <h2
                                        className="accordion-header"
                                        id="headingTwo"
                                      >
                                        <button
                                          className="accordion-button collapsed"
                                          type="button"
                                          data-bs-toggle="collapse"
                                          data-bs-target="#collapseTwo"
                                          aria-expanded="false"
                                          aria-controls="collapseTwo"
                                        >
                                          <h6 className=" mb-0">
                                            Bill Details
                                            <span>
                                              $ {item?.netValue.toFixed(2)}
                                              <span className="badge bg-primary ms-1">
                                                PAID
                                              </span>
                                            </span>
                                          </h6>
                                        </button>
                                      </h2>
                                      <div
                                        id="collapseTwo"
                                        className="accordion-collapse collapse"
                                        aria-labelledby="headingTwo"
                                        data-bs-parent="#accordionExample"
                                      >
                                        <div className="accordion-body">
                                          <div className="row mb-2">
                                            <p className="col-7 mid-gray mb-0">
                                              <small>Item Total</small>
                                            </p>
                                            <h6 className="col-5 mid-gray medium mb-0 amount">
                                              $ {item?.amount.toFixed(2)}
                                            </h6>
                                          </div>
                                          <div className="row mb-2">
                                            <p className="col-7 mid-gray mb-0">
                                              <small>Delivery Fee</small>
                                            </p>
                                            <h6 className="col-5 mid-gray medium mb-0 amount">
                                              ${" "}
                                              {item?.deliveryCharge.toFixed(2)}
                                            </h6>
                                          </div>
                                          <div className="row mb-2">
                                            <p className="col-7 mid-gray mb-0">
                                              <small>Tip Charge</small>
                                            </p>
                                            <h6 className="col-5 mid-gray medium mb-0 amount">
                                              ${" "}
                                              {item?.deliveryPersonTips.toFixed(
                                                2
                                              )}
                                            </h6>
                                          </div>
                                          <div className="row mb-2">
                                            <p className="col-7 mid-gray mb-0">
                                              <small>Taxes</small>
                                            </p>
                                            <h6 className="col-5 mid-gray medium mb-0 amount">
                                              $ {item?.tax.toFixed(2)}
                                            </h6>
                                          </div>
                                          <div className="row mb-1 mt-1">
                                            <p className="col-7 mb-0">
                                              Gross Grand Total
                                            </p>
                                            <h6 className="col-5 medium mb-0 amount">
                                              $ {item?.totalAmount.toFixed(2)}
                                            </h6>
                                          </div>
                                        </div>
                                      </div>
                                      {/* {(item.orderstatusId === 4 ||
                                        item.orderstatusId === 5) && (
                                          <div className="bg-blk mt-3">
                                            <div className="ride">
                                              <img
                                                src="/assets/images/ride-ico.svg"
                                                alt="ico"
                                              />
                                              <p className="ms-3 mb-0">
                                                <span className="mid-gray tiny">
                                                  {item.orderstatusId === 4
                                                    ? "Searching nearby riders"
                                                    : item.orderstatusId === 5
                                                      ? "Rider on the way"
                                                      : "Order delivered"}
                                                </span>
                                                <br />
                                                <strong>
                                                  OTP:{" "}
                                                  {item?.deliveryOtp ||
                                                    trackingInfo[item.uuid]
                                                      ?.deliveryOtp ||
                                                    "N/A"}
                                                </strong>
                                              </p>
                                            </div>
                                          </div>
                                        )} */}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-12 text-end mt-3">
                                  {getButtonLabel(item.orderstatus?.name).map(
                                    (label, index) => (
                                      <button
                                        key={index}
                                        className={`btn btn-${label === "Cancel Order"
                                          ? "danger"
                                          : "primary"
                                          } rounded-5 small me-3`}
                                        onClick={() =>
                                          handleOrderStatusChange(item, label)
                                        }
                                      >
                                        {label}
                                      </button>
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </section>

        </div>
      </section>
      {showNotification && (
        <CustomNotification
          message={notificationMessage}
          onClose={() => setShowNotification(false)}
        />
      )}
    </>
  );
};

export default OfflineOrders;